import { UNDO_SEND_CHOICES } from '@/api/types';
import type { ComposeSeed } from '@/routes/mail/ComposeDialog';
import { sendProblem } from './sendProblem';

/**
 * The send undo window (issue #182, doc 06 §3).
 *
 * ## Why this is its own slot and not the mail one
 *
 * Archive, delete and move already wait five seconds behind a single Undo
 * (`useMessageMutations.ts`). Their window is deliberately tied to a mounted
 * host: leaving Mail for Calendar commits the pending archive, because the
 * snackbar that offered Undo has gone with the view. A send cannot work that
 * way. Pressing Send and then going to look something up in the calendar is an
 * ordinary thing to do, and it must not shorten the window or, worse, be the
 * thing that decides when the message leaves. So the pending send lives here,
 * in a module singleton outside the React tree, and its snackbar is mounted on
 * the shell rather than on the mail view.
 *
 * ## The one decision worth arguing about: closing the page COMMITS
 *
 * The window has to survive an in-app navigation, and it has to resolve — one
 * way or the other — when the document goes away. The choice is between
 * cancelling the send and committing it, and it is not close:
 *
 *  - The user pressed Send. That is the instruction. Undo is the only thing
 *    that means "don't", and it is one click away for the whole window.
 *  - Cancelling on unload would mean a reload, a crash, or closing the tab
 *    silently unsends a message the user believes is gone. There would be no
 *    trace of it anywhere except the draft — which the user has already
 *    dismissed from their screen. That is the failure mode this feature exists
 *    to prevent, reintroduced by the feature itself.
 *  - Shortening the window to an immediate send would honour the instruction
 *    but throw away the whole point of #182.
 *
 * So `visibilitychange` → `hidden` commits, and `pagehide` is the backstop —
 * the same pair, and the same order, the mail undo uses. `HttpTransport` marks
 * a request issued while the page is hidden `keepalive`, so the POST outlives
 * the document rather than being aborted as it is torn down.
 *
 * **It cannot send twice.** There is one slot; `take()` empties it
 * synchronously before the request is issued, so `hidden` followed by
 * `pagehide` — or by the timer, or by a second Send — finds nothing left to
 * commit. The slot is memory only and is never persisted, so a reload cannot
 * replay it either: the request the dying page issued is the only one there
 * ever was. Within that single request, the transport's own `Idempotency-Key`
 * covers its one re-auth resend (ADR 0032).
 *
 * ## Failing is not losing
 *
 * If the committed send fails, the message is not discarded and it is not
 * retried behind the user's back. The whole composer — recipients, Bcc,
 * attachments, the draft id and the account it was pinned to — is handed back
 * through {@link takeRestoredSend}, with the reason attached, exactly as Undo
 * hands it back. The mail view reopens the composer on it.
 *
 * ## No host, no delay
 *
 * The window only exists while something on screen can offer Undo
 * ({@link registerSendUndoHost}) — the same rule the mail undo follows. The
 * shell's snackbar is one host; a popped-out compose window is its own (it
 * keeps the window open on a countdown with Undo, `ComposeWindowView`).
 * Anything with neither sends straight away rather than parking a message
 * behind an Undo nobody can press.
 *
 * ## How long
 *
 * Settings → General → "Undo send": 5, 10, 20 or 30 seconds, saved on the
 * server with the other per-person preferences, so the main window and a
 * popped-out composer read the same value. The composer passes it with each
 * send ({@link QueuedSend.windowMs}); this module only guards the range.
 */

/** The default window, and the one used when no preference is known. */
export const SEND_UNDO_WINDOW_MS = 5000;

/**
 * The window for a saved `undoSendSeconds`, in milliseconds. Anything that is
 * not one of the four choices — a missing field from an older server, a value
 * a mock let through — falls back to the default rather than to "no window".
 */
export function sendUndoWindowMs(seconds: number | undefined): number {
  return seconds !== undefined && (UNDO_SEND_CHOICES as readonly number[]).includes(seconds)
    ? seconds * 1000
    : SEND_UNDO_WINDOW_MS;
}

/** The least time left after the snackbar is released, so it cannot vanish on blur. */
const SEND_UNDO_RESUME_MIN_MS = 2000;

/** Everything needed to put the composer back exactly as it was. */
export interface RestoredSend {
  accountId: string;
  fromAddress: string;
  seed: ComposeSeed;
  /** Why the message came back, when it came back from a failure. */
  problem?: string;
}

/**
 * Where the copy of a sent message is: what "View message" opens (#84). Folder
 * and message ids mean something only inside one mailbox, so the mailbox
 * travels with them.
 */
export interface SentMessageLink {
  accountId: string;
  folderId: string;
  messageId: string;
}

/**
 * What a finished send has to say for itself: a sentence, and the copy in Sent
 * when the server said where it is.
 */
export interface SendOutcome {
  label: string;
  view?: SentMessageLink;
  /**
   * True for a plain "Message sent": nothing went wrong, and it is said only
   * because there is something to offer (View message). A popped-out window
   * does not stay open for one.
   */
  routine?: boolean;
}

/** What the snackbar needs to know. A new object per pending send. */
export interface PendingSendView {
  id: number;
  label: string;
  /** False for the after-the-fact notice, which has nothing left to undo. */
  undoable: boolean;
  /** On a notice: the sent message's copy, for "View message". */
  view?: SentMessageLink;
  /** On a notice: see {@link SendOutcome.routine}. */
  routine?: boolean;
  /**
   * When the pending send will commit (epoch ms), for the countdown. Absent
   * while the clock is held (pointer or focus on the snackbar) and on a notice.
   */
  deadline?: number;
  /** Seconds left while the clock is held, so a held countdown stays put. */
  heldSeconds?: number;
  /** The whole window, in seconds: the most the countdown can ever show. */
  windowSeconds?: number;
}

export interface QueuedSend {
  label: string;
  restore: Omit<RestoredSend, 'problem'>;
  /**
   * Performs the send. Rejecting hands the draft back with the reason;
   * resolving with a string says it went but not cleanly — a refused Cc, a
   * server warning — which the snackbar then says, because the composer that
   * would otherwise have shown it is gone. An outcome says the same and can
   * also carry where the copy in Sent is, for "View message".
   */
  send: () => Promise<string | SendOutcome | undefined>;
  /**
   * How long to hold it, in ms: {@link sendUndoWindowMs} of the saved
   * preference. {@link SEND_UNDO_WINDOW_MS} when absent.
   */
  windowMs?: number;
  /**
   * Told when the clock stops (pointer or focus on the snackbar) and starts
   * again. The Outbox extends its durable hold here, so a window paused for
   * longer than the hold was sized for is not flushed out from under Undo.
   */
  onHoldChange?: (held: boolean) => void;
  /**
   * Called instead of {@link QueuedSend.send} when the user presses Undo.
   *
   * The send path may have written something durable before the window opened
   * — the Outbox does exactly that, because this module commits on unload and
   * an IndexedDB write racing a document teardown is the one write that cannot
   * be relied on (`features/outbox/types.ts`). Undo has to be able to take that
   * back, and only the caller knows what "that" was.
   */
  onUndo?: () => void;
}

interface Entry extends PendingSendView {
  restore: Omit<RestoredSend, 'problem'>;
  send: () => Promise<string | SendOutcome | undefined>;
  onUndo: (() => void) | undefined;
  onHoldChange: ((held: boolean) => void) | undefined;
  timer: ReturnType<typeof setTimeout> | undefined;
  deadline: number;
  remaining: number;
  windowMs: number;
  held: boolean;
}

let pending: Entry | undefined;
let pendingView: PendingSendView | undefined;
/** A sentence with no Undo left in it, shown after a send that went imperfectly. */
let notice: PendingSendView | undefined;
let noticeTimer: ReturnType<typeof setTimeout> | undefined;
let nextId = 1;
let hostCount = 0;
/** Committed sends whose request has not settled yet. */
let inFlight = 0;
const pendingListeners = new Set<() => void>();

let restored: RestoredSend | undefined;
const restoredListeners = new Set<() => void>();

function emitPending(): void {
  pendingView = pending
    ? {
        id: pending.id,
        label: pending.label,
        undoable: true,
        windowSeconds: Math.ceil(pending.windowMs / 1000),
        ...(pending.held
          ? { heldSeconds: Math.ceil(pending.remaining / 1000) }
          : { deadline: pending.deadline }),
      }
    : undefined;
  for (const listener of pendingListeners) listener();
}

function emitAll(): void {
  for (const listener of pendingListeners) listener();
}

/** How long an after-the-fact notice stays on screen. */
const NOTICE_MS = 8000;

/**
 * Says something about a send that has already gone. Cleared by the next send.
 *
 * Exported for a send that never waited behind Undo (the composer's immediate
 * path): the composer closes as it succeeds, so a sentence it kept for itself
 * would never be read. This strip outlives it.
 */
export function showSendNotice(outcome: string | SendOutcome): void {
  const { label, view, routine } = typeof outcome === 'string' ? { label: outcome } : outcome;
  clearTimeout(noticeTimer);
  notice = {
    id: nextId++,
    label,
    undoable: false,
    ...(view !== undefined ? { view } : {}),
    ...(routine === true ? { routine: true } : {}),
  };
  noticeTimer = setTimeout(clearNotice, NOTICE_MS);
  for (const listener of pendingListeners) listener();
}

/** Dismisses the notice. Exported for the snackbar's Escape. */
export function clearNotice(): void {
  clearTimeout(noticeTimer);
  noticeTimer = undefined;
  if (notice === undefined) return;
  notice = undefined;
  for (const listener of pendingListeners) listener();
}

/** `useSyncExternalStore` pair for the snackbar. */
export function subscribePendingSend(listener: () => void): () => void {
  pendingListeners.add(listener);
  return () => {
    pendingListeners.delete(listener);
  };
}

export function getPendingSend(): PendingSendView | undefined {
  return pendingView ?? notice;
}

/**
 * How many committed sends are still on the wire. A popped-out composer waits
 * for this to reach zero before it closes its window, because closing the
 * document mid-request is how a send is lost.
 */
export function getSendsInFlight(): number {
  return inFlight;
}

/**
 * Declares that something on screen can offer Undo for a send. Returns the
 * release; the last release commits whatever is pending, because a window
 * nobody can see is not a window.
 */
export function registerSendUndoHost(): () => void {
  hostCount += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    hostCount -= 1;
    if (hostCount === 0) commitPendingSend();
  };
}

/** False when a send must go out at once: no snackbar, no delay. */
export function sendUndoAvailable(): boolean {
  return hostCount > 0;
}

/**
 * Hands a composer back to whoever is listening — the mail view, which
 * reopens it. Stashed as well as announced, so a listener that mounts
 * afterwards (Undo pressed from Calendar, then a navigation to Mail) still
 * finds it.
 */
function handBack(value: RestoredSend): void {
  restored = value;
  for (const listener of restoredListeners) listener();
}

export function subscribeRestoredSend(listener: () => void): () => void {
  restoredListeners.add(listener);
  return () => {
    restoredListeners.delete(listener);
  };
}

/** Takes the composer waiting to be reopened, if there is one. */
export function takeRestoredSend(): RestoredSend | undefined {
  const value = restored;
  restored = undefined;
  return value;
}

function take(): Entry | undefined {
  const entry = pending;
  if (!entry) return undefined;
  clearTimeout(entry.timer);
  pending = undefined;
  setLifecycleListeners(false);
  emitPending();
  return entry;
}

/** Sends the pending message now (the timer, leaving, a second Send, unload). */
export function commitPendingSend(): void {
  const entry = take();
  if (!entry) return;
  inFlight += 1;
  emitAll();
  entry
    .send()
    .then((outcome) => {
      if (outcome !== undefined) showSendNotice(outcome);
    })
    .catch((error: unknown) => {
      // Not lost and not retried: the message comes back as a composer with
      // the reason in it, which is the only outcome the user can act on --
      // over the send rate (#83), with how long to wait.
      handBack({
        ...entry.restore,
        problem: sendProblem(error),
      });
    })
    .finally(() => {
      inFlight -= 1;
      emitAll();
    });
}

/** Cancels the pending send and gives the composer back. False when there was none. */
export function undoPendingSend(): boolean {
  const entry = take();
  if (!entry) return false;
  entry.onUndo?.();
  handBack(entry.restore);
  return true;
}

/** Stops (true) or restarts (false) the pending send's clock. */
export function holdPendingSend(held: boolean): void {
  const entry = pending;
  if (!entry || entry.held === held) return;
  entry.held = held;
  if (held) {
    clearTimeout(entry.timer);
    entry.timer = undefined;
    entry.remaining = Math.max(0, entry.deadline - Date.now());
  } else {
    startClock(entry, Math.max(entry.remaining, SEND_UNDO_RESUME_MIN_MS));
  }
  entry.onHoldChange?.(held);
  emitPending();
}

function startClock(entry: Entry, ms: number): void {
  clearTimeout(entry.timer);
  entry.deadline = Date.now() + ms;
  entry.remaining = ms;
  entry.timer = setTimeout(commitPendingSend, ms);
}

function onVisibilityChange(): void {
  if (document.visibilityState === 'hidden') commitPendingSend();
}

function setLifecycleListeners(on: boolean): void {
  if (typeof window === 'undefined') return;
  if (on) {
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', commitPendingSend);
  } else {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('pagehide', commitPendingSend);
  }
}

/**
 * Parks a send behind the undo window.
 *
 * A second Send finalises the first, for the same reason a second archive
 * does: two pending messages and one Undo is a control that cannot say what
 * it will do.
 */
export function queueSend(queued: QueuedSend): void {
  commitPendingSend();
  clearNotice();

  const windowMs = queued.windowMs ?? SEND_UNDO_WINDOW_MS;
  const entry: Entry = {
    id: nextId++,
    label: queued.label,
    undoable: true,
    restore: queued.restore,
    send: queued.send,
    onUndo: queued.onUndo,
    onHoldChange: queued.onHoldChange,
    timer: undefined,
    deadline: 0,
    remaining: windowMs,
    windowMs,
    held: false,
  };

  pending = entry;
  startClock(entry, windowMs);
  setLifecycleListeners(true);
  emitPending();
}
